import type { ChatMessage, ChatSendMessageType } from '@/types/chat';
import { MessageRole } from '@/constants';

export type SuggestionArrowKind = 'follow_up' | 'level_up';

export type PendingSuggestionPick = { source: SuggestionArrowKind; prompt: string };
export type PendingToolkitPromptPick = { prompt: string };

export function resolveChatSendMessageType(params: {
  /** When replaying a deferred send, use the value captured at enqueue time */
  precomputed?: ChatSendMessageType;
  normalizedPrompt: string;
  normalize: (s: string) => string;
  suggestionArrow?: SuggestionArrowKind;
  improvedPrompt: string | null;
  hasUneditedImprovedPrompt: boolean;
  messages: ChatMessage[];
  pendingSuggestion: PendingSuggestionPick | null;
  pendingToolkitPrompt: PendingToolkitPromptPick | null;
}): ChatSendMessageType {
  const {
    precomputed,
    normalizedPrompt,
    normalize,
    suggestionArrow,
    improvedPrompt,
    hasUneditedImprovedPrompt,
    messages,
    pendingSuggestion,
    pendingToolkitPrompt,
  } = params;

  if (precomputed) return precomputed;

  if (suggestionArrow === 'follow_up') return 'FOLLOW_UP_SUGGESTION';
  if (suggestionArrow === 'level_up') return 'LEVEL_UP_SUGGESTION';

  if (pendingToolkitPrompt) {
    const np = normalize(pendingToolkitPrompt.prompt);
    return np === normalizedPrompt ? 'TOOLKIT_PROMPT' : 'TOOLKIT_PROMPT_EDITED';
  }

  const lastAssistant = [...messages].reverse().find((m) => m.role === MessageRole.Assistant);
  const followUps = lastAssistant?.suggestions?.map((s) => s.prompt) ?? [];
  const levelUps = lastAssistant?.levelUp?.map((l) => l.prompt) ?? [];

  if (levelUps.some((p) => normalize(p) === normalizedPrompt)) {
    return 'LEVEL_UP_SUGGESTION';
  }
  if (followUps.some((p) => normalize(p) === normalizedPrompt)) {
    return 'FOLLOW_UP_SUGGESTION';
  }

  if (pendingSuggestion) {
    const np = normalize(pendingSuggestion.prompt);
    if (np === normalizedPrompt) {
      return pendingSuggestion.source === 'level_up' ? 'LEVEL_UP_SUGGESTION' : 'FOLLOW_UP_SUGGESTION';
    }
    return pendingSuggestion.source === 'level_up' ? 'LEVEL_UP_SUGGESTION_EDITED' : 'FOLLOW_UP_SUGGESTION_EDITED';
  }

  if (improvedPrompt) {
    const ni = normalize(improvedPrompt);
    if (hasUneditedImprovedPrompt && normalizedPrompt === ni) {
      return 'IMPROVED_PROMPT';
    }
    if (!hasUneditedImprovedPrompt) {
      return 'IMPROVED_PROMPT_EDITED';
    }
  }

  return 'USER_MESSAGE';
}
